import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { clubPost } from "./clubApi";
import { consoleColors, gridSx, primaryButtonSx } from "../admin/consoleTheme";
import IndiaDataGrid from "../../include/IndiaDataGrid";
import { formatAmount } from "../../include/money";

const columns = [
  { field: "date_added", headerName: "Date", width: 170 },
  { field: "store_name", headerName: "Used at", flex: 1, minWidth: 140 },
  { field: "customer_name", headerName: "Customer", width: 150 },
  { field: "customer_mobile", headerName: "Mobile", width: 130 },
  { field: "points", headerName: "Points", width: 90 },
  { field: "value", headerName: "Value", width: 100 },
  { field: "source_text", headerName: "Taken from", flex: 1.6, minWidth: 220 },
  { field: "order_amount", headerName: "Bill", width: 110 },
];

export default function ClubLoyaltyHistory() {
  const { clubData } = useContext(DataContext);
  const clubId = clubData?.club_info?.club_id || "";
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [usedHere, setUsedHere] = useState([]);
  const [takenFrom, setTakenFrom] = useState([]);
  const [totals, setTotals] = useState({ points: 0, value: 0, uses: 0 });
  const [storeId, setStoreId] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = useCallback(async () => {
    if (!clubId) return;
    const result = await clubPost("CLUB_LOYALTY_HISTORY", {
      club_id: clubId,
      store_id: storeId,
      searchKey,
      startDate,
      endDate,
      start: 0,
      limit: 300,
    });
    if (result.status !== "200") {
      toast.error(result?.status_message || "Could not load loyalty history");
      return;
    }
    const data = result.data || {};
    setRows(data.rows || []);
    setStores(data.stores || []);
    setUsedHere(data.used_here || []);
    setTakenFrom(data.taken_from || []);
    setTotals({
      points: Number(data.total_points || 0),
      value: Number(data.total_value || 0),
      uses: Number(data.total_uses || 0),
    });
  }, [clubId, storeId, searchKey, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography sx={{ color: consoleColors.muted, fontSize: 14, mb: 2, lineHeight: 1.5 }}>
        Stores in this club can spend each other’s full loyalty balance, except stores with the same business type.
        Each use shows how many points came from which store, for example 10 from Store 1 · 10 from Store 2.
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center", background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 1.5 }}>
        <TextField select size="small" label="Used at store" value={storeId} onChange={(e) => setStoreId(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">All stores</MenuItem>
          {stores.map((store) => (
            <MenuItem key={store.store_id} value={store.store_id}>
              {store.store_name}{store.business_type_name ? ` · ${store.business_type_name}` : ""}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Customer name or mobile" value={searchKey} onChange={(e) => setSearchKey(e.target.value)} sx={{ minWidth: 220 }} />
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <Button variant="contained" onClick={load} sx={primaryButtonSx}>Apply</Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5, mb: 2 }}>
        <SummaryCard label="Points used" value={totals.points} />
        <SummaryCard label="Value used" value={`₹${formatAmount(totals.value)}`} />
        <SummaryCard label="Uses" value={totals.uses} />
      </Box>

      {(usedHere.length > 0 || takenFrom.length > 0) && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, mb: 2 }}>
          <StoreSums title="Used at this store" hint="Total points this customer (or all customers) spent while billing at each store." rows={usedHere} />
          <StoreSums title="Taken from this store" hint="Points cut from each store’s customer balance, including when used at another club store." rows={takenFrom} />
        </Box>
      )}

      <Box sx={{ height: 640, background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, overflow: "hidden" }}>
        <IndiaDataGrid
          rows={rows.map((row) => ({ ...row, id: row.use_id }))}
          columns={columns}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          sx={gridSx}
        />
      </Box>
    </Box>
  );
}

function SummaryCard({ label, value }) {
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2 }}>
      <Typography sx={{ fontSize: 12, color: consoleColors.muted, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 750, color: consoleColors.ink, lineHeight: 1.2, mt: 0.4 }}>{value}</Typography>
    </Box>
  );
}

function StoreSums({ title, hint, rows }) {
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 750 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: consoleColors.muted, mt: 0.4, mb: 1.2, lineHeight: 1.45 }}>{hint}</Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>No uses in this filter.</Typography>
      ) : rows.map((row) => (
        <Box key={row.store_id} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.7, borderTop: `1px solid ${consoleColors.line}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 650 }}>{row.store_name}</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 750 }}>{row.points} pts{row.uses ? ` · ${row.uses} uses` : ""}</Typography>
        </Box>
      ))}
    </Box>
  );
}
